import React from 'react';
import moment from 'moment';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { Link } from 'ui/base/link';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';

import { SearchInput } from 'ui/standard/input/search_input/search_input';
import { SearchInputStore } from 'ui/standard/input/search_input/search_input_store';

import { BasePath } from 'services/base/page_path';

import { Pangolin } from 'services/accounts/types';
import { Host, HostMap } from 'services/pangolin/types';

import {
  hostDisplayDeviceType,
  hostDisplayName,
  hostMatchesSearchString,
} from 'services/pangolin/hosts_service';

import {
  themedDeviceTypeIconClassStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import Messages from './device_search_messages';
import baseStyles from './device_search.module.css';

type SearchIconStyles = {
  iconButton: string;
  searchIcon: string;
  devicesContainer: string;
  deviceIcon: string;
  deviceDetailColumn: string;
  primaryDetail: string;
  secondaryDetail: string;
  link: string;
};

const themedStyles: ThemedStyles<SearchIconStyles> = {
  [Theme.DAYLIGHT]: {
    iconButton: baseStyles.iconButton,
    searchIcon: baseStyles.daylightSearchIcon,
    devicesContainer: baseStyles.devicesContainer,
    deviceIcon: baseStyles.deviceIcon,
    deviceDetailColumn: baseStyles.deviceDetailColumn,
    primaryDetail: baseStyles.daylightPrimaryDetail,
    secondaryDetail: baseStyles.daylightSecondaryDetail,
    link: baseStyles.daylightLink,
  },
  [Theme.MIDNIGHT]: {
    iconButton: baseStyles.iconButton,
    searchIcon: baseStyles.midnightSearchIcon,
    devicesContainer: baseStyles.devicesContainer,
    deviceIcon: baseStyles.deviceIcon,
    deviceDetailColumn: baseStyles.deviceDetailColumn,
    primaryDetail: baseStyles.midnightPrimaryDetail,
    secondaryDetail: baseStyles.midnightSecondaryDetail,
    link: baseStyles.midnightLink,
  },
};

type DeviceItemProps = {
  pangolin: Pangolin;
  data: Host;
  key: string;
};

export const DeviceItem = ({ pangolin, data: host }: DeviceItemProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);
  const deviceIconClass = getDeviceTypeIcon(deviceIcons, hostDisplayDeviceType(host));
  return (
    <Link path={{ kind: BasePath.DEVICES_VIEW, pangolinID: pangolin.id, deviceID: host.id }} className={styles.link}>
      <div className={styles.devicesContainer}>
        <div>
          <div className={classNames(styles.deviceIcon, deviceIconClass)}/>
        </div>
        <div className={styles.deviceDetailColumn}>
          <div className={styles.primaryDetail}>{hostDisplayName(host)}</div>
          <div className={styles.secondaryDetail}>
            <span>{host.ip || host.mac}</span>
            <span>{moment(host.lastSeen).fromNow()}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

type DeviceSearchDisplayProps = {
  store: SearchInputStore;
};

const DeviceSearchDisplay = ({ store }: DeviceSearchDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.modal.showModal();
  }, [store]);

  return (
    <button type='button' className={styles.iconButton} onClick={onShowModal}>
      <div className={styles.searchIcon}/>
    </button>
  );
};

const getIpOrMac = (host: Host) => {
  if (host.ip) {
    return host.ip;
  } else if (host.mac) {
    return host.mac;
  }
  return '';
};

type DeviceSearchProps = {
  pangolin: Pangolin;
  hosts: HostMap;
  store: SearchInputStore;
};

export const DeviceSearch = observer(({ hosts, pangolin, store }: DeviceSearchProps) => {
  const DisplayComponent = React.useCallback(() => (
    <DeviceSearchDisplay store={store}/>
  ), [store]);

  const ItemComponent = React.useCallback((props: Omit<DeviceItemProps, 'pangolin'>) => (
    <DeviceItem pangolin={pangolin} {...props}/>
  ), [pangolin]);

  return (
    <SearchInput
      selectOnly={false}
      placeholder={Messages.placeholder()}
      store={store}
      data={hosts.values}
      filterFunc={hostMatchesSearchString}
      keyFunc={getIpOrMac}
      ItemComponent={ItemComponent}
      DisplayComponent={DisplayComponent}
    />
  );
});